import { css } from 'styled-components';

import { Flex } from '@/components/base';
import ExpandSearchInput from '@/components/Inputs/ExpandSearchInput';
import { useSearch } from '@/components/Inputs/useSearch';
import TabRadio from '@/components/TabRadio/TabRadio';
import AMP_KEYS from '@/configs/amp-track-keys';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import DatePresetFilter, {
  type DateFilterState,
  useDatePresetFilter,
} from '@/features/Commons/DatePresetFilter';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import { PageHeader } from '@/shared/ui';
import useInsightExplorerStore, {
  initialDateState,
} from '@/stores/InsightExplorerStore';
import getDatePreset from '@/utils/getDatePreset';
import keymap from '@/utils/keymap';
import { track } from '@/utils/track/track';

import {
  type InsightExplorerViewType,
  InsightExplorerViewTypeMap,
} from '../../types/constants';
import SentimentGuideTooltip from './SentimentGuideTooltip/SentimentGuideTooltip';

const InsightExplorerHeader = () => {
  const { view, setState } = useInsightExplorerStore();

  const { dateState, onDateFilterChange } = useDatePresetFilter({
    key: STORAGE_KEYS.INSIGHT_EXPLORER_DATE_FILTER,
    initialValue: initialDateState,
  });

  const onDateFilterChangeHandler = (state: DateFilterState) => {
    onDateFilterChange(state);
    setState({
      dateState: state,
    });
  };

  const { filterQueryFormats, onFilterSetChange, clearFilter } =
    useStructuredFilterQuery({
      onFilterChangeCallback(formats) {
        if (formats.length > 0) {
          track(AMP_KEYS.INSIGHT_EXPLORER_FILTER_CHANGE, {
            column: formats[formats.length - 1]?.columnKey,
            value: formats[formats.length - 1].filterValues.join(','),
          });
        }
        setState({ filterQueryFormats: formats });
      },
      clearFilterCallback() {
        track(AMP_KEYS.INSIGHT_EXPLORER_FILTER_CLEAR);
        setState({ filterQueryFormats: [] });
      },
    });

  const { initSearchValue, onKeyEnterSearch } = useSearch();

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (keymap.isEnter(e)) {
      track(AMP_KEYS.INSIGHT_EXPLORER_KEYWORD_FILTER_ENTER, {
        keyword: e.currentTarget.value,
      });
      onKeyEnterSearch(e);
      setState({ searchValue: e.currentTarget.value });
    }
  };

  const handleClearSearch = () => {
    initSearchValue();
    setState({ searchValue: '' });
  };

  const handleViewChange = (key: InsightExplorerViewType) => {
    track(AMP_KEYS.INSIGHT_EXPLORER_VIEW_SELECT, {
      view: InsightExplorerViewTypeMap.get(key),
    });
    setState({ view: key });
  };

  return (
    <PageHeader
      title="Insight Explorer"
      topRightNode={
        <Flex>
          <TabRadio
            options={InsightExplorerViewTypeMap.toArray().map((op) => {
              return {
                key: op.value,
                node: <div>{op.label}</div>,
              };
            })}
            value={view}
            onChange={(key) => {
              handleViewChange(key);
            }}
          />
          <SentimentGuideTooltip
            style={{
              position: 'fixed',
              top: 68,
              right: 32,
            }}
          />
        </Flex>
      }
      bottomNode={
        <Flex g={32} a="center" j="space-between" f={1}>
          <Flex>
            <StructuredFilter
              clearFilter={clearFilter}
              values={filterQueryFormats}
              onChange={onFilterSetChange}
            />
          </Flex>
          <Flex j="flex-end" g={20} w="wrap" f={1}>
            <DatePresetFilter
              dateState={dateState}
              setDateState={(state) => {
                onDateFilterChangeHandler(state);
                track(AMP_KEYS.INSIGHT_EXPLORER_DATE_FILTER_CHANGE, {
                  date_range: state.dateRange.join(','),
                  range_unit: state.rangeUnit,
                  preset_item: getDatePreset(state.rangeUnit, true).find(
                    (preset) => preset.value === state.presetItem,
                  )?.name,
                });
              }}
              size="middle"
            />
            <ExpandSearchInput
              rounded
              placeholder="Find in feedback"
              sizeType="m"
              onKeyDown={handleSearch}
              onClearInput={handleClearSearch}
              css={css`
                max-width: 200px;
              `}
            />
          </Flex>
        </Flex>
      }
      style={{
        paddingBottom: 16,
      }}
    />
  );
};

export default InsightExplorerHeader;
